import styled from '@emotion/styled';
import { Formik } from 'formik';
import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import Page from 'App/components/Page';
import TherigyLoadingSpinner from 'App/components/TherigyLoadingSpinner/TherigyLoadingSpinner';
import useFetchAdminTherapyCategoriesQuery from 'App/hooks/useFetchAdminTherapyCategoriesQuery';
import useFetchMedicationQuery from 'App/hooks/useFetchMedicationQuery';
import { upsertMedicationAndGPI10Mapping } from 'App/services/AdminService';
import useIsPermitted from 'App/utils/hooks/useIsPermitted';
import { PERMISSION_TYPES } from 'App/utils/isPermitted';
import toast from 'Shared/toast';

import AddEditMedicationDetailsForm from './components/AddEditMedicationDetailsForm';
import FindMedispanMedicationForm from './components/FindMedispanMedicationForm';
import ActionButtons from './components/ActionButtons';
import Error from './components/Error';

const CenterWrapper = styled('div')`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
`;

const validationSchema = yup.object().shape({
    assignedTherapeuticCategories: yup.array(),
    gpi10: yup.string(),
    stmMedicationName: yup.string().required('STM Medication Name is required'),
});

const defaultMedispanValues = {
    hasSearched: false,
    medicationName: '',
    medispanMedicationData: { results: [], total: 0 },
    searchParams: { sortKey: 'productName', sortOrder: 'asc', count: 10, page: 0 },
};

const EditMedication = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [gpi10Saved, setGpi10Saved] = useState(false);
    const isClinicalAdmin = useIsPermitted(PERMISSION_TYPES.CLINICAL_ADMIN);
    const permissionType = isClinicalAdmin
        ? PERMISSION_TYPES.ENABLE_CODIFY_MEDICATIONS
        : PERMISSION_TYPES.CLINICAL_ADMIN;
    const navigate = useNavigate();
    const location = useLocation();
    const initialMedicationName = location.state?.medication?.name;
    const existingMedicationNameRef = useRef(initialMedicationName);

    const { data: medications, isFetching } = useFetchMedicationQuery(initialMedicationName);
    const { data: therapeuticCategories, isLoading: isCategoriesLoading } = useFetchAdminTherapyCategoriesQuery();

    const navigateBack = () =>
        navigate(`/therapeutic-categories${location.state?.therapyId ? `/${location.state.therapyId}` : ''}`);

    const assignedTherapeuticCategories =
        medications && therapeuticCategories
            ? [...new Set(medications?.map((med) => med.groupId).filter(Boolean))]
                  .map((groupId) => therapeuticCategories[groupId])
                  .filter(Boolean)
            : [];

    const hasGpi10 = !!medications?.[0]?.gpi10;

    const initialValues = {
        assignedTherapeuticCategories,
        gpi10: medications?.[0]?.gpi10 || '',
        ...defaultMedispanValues,
        stmMedicationName: initialMedicationName,
    };

    return (
        <Page title="Edit STM Medication" showPageTitle permissionType={permissionType}>
            {isFetching || isCategoriesLoading ? (
                <CenterWrapper>
                    <TherigyLoadingSpinner />
                </CenterWrapper>
            ) : (
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onReset={() => setErrorMessage(null)}
                    onSubmit={async (values, { setSubmitting, resetForm }) => {
                        const newMedicationName = values.stmMedicationName?.trim();
                        const therapeuticCategoryIds = values.assignedTherapeuticCategories.map(
                            (therapy) => therapy?.id || therapy?.value
                        );
                        const gpi10 = values.gpi10 || null;
                        setErrorMessage(null);
                        try {
                            await upsertMedicationAndGPI10Mapping({
                                existingMedicationName: existingMedicationNameRef.current,
                                gpi10,
                                isEditMode: true,
                                newMedicationName,
                                therapeuticCategoryIds,
                            });
                            toast.success('The medication has been updated successfully.');
                            existingMedicationNameRef.current = newMedicationName;
                            if (gpi10) {
                                setGpi10Saved(true);
                            }
                            resetForm({
                                values: {
                                    ...values,
                                    ...defaultMedispanValues,
                                },
                            });
                        } catch (error) {
                            setErrorMessage(
                                error?.response?.data?.message || 'Failed to update medication. Please try again.'
                            );
                        } finally {
                            setSubmitting(false);
                        }
                    }}
                >
                    {({ handleSubmit, resetForm }) => {
                        return (
                            <>
                                <AddEditMedicationDetailsForm isEditMode />
                                {!hasGpi10 && !gpi10Saved && <FindMedispanMedicationForm />}
                                <Error errorMessage={errorMessage} />
                                <ActionButtons onReset={resetForm} onCancel={navigateBack} onSave={handleSubmit} />
                            </>
                        );
                    }}
                </Formik>
            )}
        </Page>
    );
};

export default EditMedication;
